import { Reveal } from "./Reveal";
import { Croissant, CupSoda, Candy, Milk, IceCream, Cookie } from "lucide-react";
import { useLang } from "../i18n";

const APP_ICONS = [Croissant, CupSoda, Candy, Milk, IceCream, Cookie];
const APP_SLUGS = ["bakery", "beverages", "confectionery", "dairy", "ice-cream", "chocolate-coatings"];

export const Applications = ({ h1 = false }) => {
  const { t } = useLang();
  const H = h1 ? "h1" : "h2";
  return (
  <section id="applications" data-testid="applications-section" className="bg-clara-cream py-24 lg:py-36">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
      <Reveal>
        <p className="eyebrow text-clara-terracotta mb-6">{t.applications.eyebrow}</p>
        <H className="font-display font-light text-5xl lg:text-7xl leading-[0.95] tracking-tighter text-clara-espresso max-w-3xl mb-16">
          {t.applications.headingA} <span className="italic text-clara-terracotta">{t.applications.headingEm}</span>
        </H>
      </Reveal>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-clara-espresso/15 border border-clara-espresso/15">
        {t.applications.items.map((a, i) => {
          const Icon = APP_ICONS[i];
          return (
          <Reveal key={i} delay={(i % 3) * 0.08} className="bg-clara-cream">
            <div className="group h-full p-8 hover:bg-clara-espresso transition-colors duration-500" data-testid={`application-${APP_SLUGS[i]}`}>
              <Icon className="w-8 h-8 text-clara-terracotta mb-6" strokeWidth={1.25} />
              <h3 className="font-display text-3xl text-clara-espresso group-hover:text-clara-cream transition-colors duration-500 mb-3">{a.name}</h3>
              <p className="text-sm text-clara-muted group-hover:text-clara-darkmuted transition-colors duration-500 leading-relaxed">{a.desc}</p>
            </div>
          </Reveal>
          );
        })}
      </div>
    </div>
  </section>
  );
};

const CERT_IMGS = ["/cert-halal.png", "/cert-iso.png", "/cert-iso22000.png", "/cert-sni.png", "/cert-bpom.png"];

export const Quality = ({ h1 = false, heading = null }) => {
  const { t } = useLang();
  const H = h1 ? "h1" : "h2";
  return (
  <section id="quality" data-testid="quality-section" className="bg-clara-surface text-clara-cream py-24 lg:py-36 noise-overlay relative">
    <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10">
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <Reveal>
            {!heading && <p className="eyebrow text-clara-terracotta mb-6">{t.quality.eyebrow}</p>}
            <H className="font-display font-light text-5xl lg:text-6xl leading-[0.95] tracking-tighter mb-6">
              {heading ?? <>{t.quality.headingA}<span className="italic text-clara-terracotta">{t.quality.headingEm}</span></>}
            </H>
            <p className="text-clara-darkmuted leading-relaxed">
              {t.quality.intro}
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-8 space-y-px">
          {t.quality.items.map((c, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className="grid grid-cols-[auto_1fr] gap-6 items-start border-t border-clara-cream/15 py-8" data-testid={`cert-${i}`}>
                <div className="bg-clara-cream rounded-lg h-20 w-20 flex items-center justify-center p-3 shrink-0">
                  <img src={CERT_IMGS[i]} alt={c.name} loading="lazy" decoding="async" className="max-h-full max-w-full object-contain" />
                </div>
                <div>
                  <h3 className="font-display text-3xl mb-2">{c.name}</h3>
                  <p className="text-clara-darkmuted leading-relaxed max-w-2xl">{c.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
  );
};
